import { cn } from "@/lib/utils";
import { Input } from "./Input";
import { Textarea } from "./Textarea";
import { Checkbox } from "./Checkbox";
import { Switch } from "./Switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./Select";
import { FormField, FormFieldLabel, FormFieldInput } from "./FormField";
import { FormHelperText } from "./FormHelperText";

// Text Input Field
interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  id?: string;
  placeholder?: string;
  required?: boolean;
  type?: string;
  maxLength?: number;
  className?: string;
  helperText?: React.ReactNode;
  error?: string;
  disabled?: boolean;
  readOnly?: boolean;
}

export function TextField({
  label,
  value,
  onChange,
  id,
  placeholder,
  required,
  type = "text",
  maxLength,
  className,
  helperText,
  error,
  disabled,
  readOnly,
  ...additionalProps
}: TextFieldProps & Record<string, any>) {
  const fieldId = id || label.toLowerCase().replace(/\s+/g, "-");

  return (
    <FormField>
      <FormFieldLabel htmlFor={fieldId}>{label}</FormFieldLabel>
      <FormFieldInput>
        <Input
          id={fieldId}
          name={fieldId}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          maxLength={maxLength}
          className={className}
          disabled={disabled}
          readOnly={readOnly}
          {...additionalProps}
        />
        {error && <p className="text-sm font-medium text-destructive pt-2">{error}</p>}
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormFieldInput>
    </FormField>
  );
}

// Number Input Field
interface NumberFieldProps extends Omit<TextFieldProps, "type" | "onChange" | "value"> {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
}

export function NumberField({ value, onChange, min, max, ...props }: NumberFieldProps) {
  return (
    <TextField
      {...props}
      value={value.toString()}
      type="number"
      onChange={(value) => onChange(parseInt(value) || 0)}
      min={min}
      max={max}
    />
  );
}

// Textarea Field
interface TextareaFieldProps extends Omit<TextFieldProps, "type"> {
  rows?: number;
}

export function TextareaField({
  label,
  value,
  onChange,
  id,
  placeholder,
  required,
  maxLength,
  className,
  helperText,
  error,
  disabled,
  readOnly,
  rows = 3,
}: TextareaFieldProps) {
  const fieldId = id || label.toLowerCase().replace(/\s+/g, "-");

  return (
    <FormField>
      <FormFieldLabel htmlFor={fieldId}>{label}</FormFieldLabel>
      <FormFieldInput>
        <Textarea
          id={fieldId}
          name={fieldId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          maxLength={maxLength}
          className={className}
          disabled={disabled}
          readOnly={readOnly}
          rows={rows}
        />
        {error && <p className="text-sm font-medium text-destructive pt-2">{error}</p>}
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormFieldInput>
    </FormField>
  );
}

// Select Field
interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
  id?: string;
  className?: string;
  helperText?: React.ReactNode;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
}

export function SelectField({
  label,
  value,
  onChange,
  options,
  id,
  className,
  helperText,
  error,
  disabled,
  placeholder,
}: SelectFieldProps) {
  const fieldId = id || label.toLowerCase().replace(/\s+/g, "-");

  return (
    <FormField>
      <FormFieldLabel htmlFor={fieldId}>{label}</FormFieldLabel>
      <FormFieldInput>
        <Select value={value} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger id={fieldId} className={cn("w-full", className)} aria-invalid={!!error}>
            <SelectValue placeholder={placeholder || "Select an option"} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {error && <p className="text-sm font-medium text-destructive pt-2">{error}</p>}
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormFieldInput>
    </FormField>
  );
}

// Checkbox Field
interface CheckboxFieldProps {
  label: React.ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  id?: string;
  helperText?: React.ReactNode;
  disabled?: boolean;
}

export function CheckboxField({
  label,
  checked,
  onChange,
  id,
  helperText,
  disabled,
}: CheckboxFieldProps) {
  const fieldId =
    id || (typeof label === "string" ? label.toLowerCase().replace(/\s+/g, "-") : "checkbox");

  return (
    <FormField>
      <FormFieldInput>
        <label className="flex items-center gap-2 cursor-pointer">
          <Checkbox id={fieldId} checked={checked} onCheckedChange={onChange} disabled={disabled} />
          <span className="select-none font-medium text-sm text-foreground">{label}</span>
        </label>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormFieldInput>
    </FormField>
  );
}

// Range Field
interface RangeFieldProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  id?: string;
  helperText?: React.ReactNode;
  formatValue?: (value: number) => string;
}

export function RangeField({
  label,
  value,
  onChange,
  min,
  max,
  id,
  helperText,
  formatValue,
}: RangeFieldProps) {
  const fieldId = id || label.toLowerCase().replace(/\s+/g, "-");

  return (
    <FormField>
      <FormFieldLabel htmlFor={fieldId}>{label}</FormFieldLabel>
      <FormFieldInput>
        <input
          id={fieldId}
          type="range"
          value={value}
          onChange={(e) => onChange(e.target.valueAsNumber)}
          min={min}
          max={max}
          className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-muted [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:border-0"
        />
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormFieldInput>
    </FormField>
  );
}

// Switch Field
interface SwitchFieldProps {
  label: React.ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  id?: string;
  helperText?: React.ReactNode;
  disabled?: boolean;
}

export function SwitchField({
  label,
  checked,
  onChange,
  id,
  helperText,
  disabled,
}: SwitchFieldProps) {
  const fieldId =
    id || (typeof label === "string" ? label.toLowerCase().replace(/\s+/g, "-") : "switch");

  return (
    <div className="flex items-start justify-between gap-4">
      <label htmlFor={fieldId} className="flex flex-col flex-1 cursor-pointer">
        <span className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
          {label}
        </span>
        {helperText && <span className="text-sm text-muted-foreground mt-1">{helperText}</span>}
      </label>
      <Switch
        id={fieldId}
        checked={checked}
        onCheckedChange={onChange}
        disabled={disabled}
        className="mt-0.5"
      />
    </div>
  );
}
